import { useState } from "react";
import { FileDown, Loader2, AlertTriangle, CheckCircle2, XCircle, MinusCircle, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO = {
  coincide: { label: "Coincide", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  no_coincide: { label: "No coincide", cls: "border-red-200 bg-red-50 text-red-700", Icon: XCircle },
  sin_dato: { label: "Sin dato", cls: "border-slate-200 bg-slate-50 text-slate-500", Icon: MinusCircle },
};

function EstadoBadge({ estado, testid }) {
  const e = ESTADO[estado] || ESTADO.sin_dato;
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${e.cls}`}>
      <e.Icon className="h-3 w-3" /> {e.label}
    </span>
  );
}

function Dato({ label, value, mono, testid }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`break-words text-sm font-medium text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</p>
    </div>
  );
}

export function DatosRegistro({ registro: r }) {
  const cargo = [r.fecha_cargo_ini, r.fecha_cargo_fin].filter(Boolean).join(" – ");
  const opcionales = [
    ["Dependencia", r.dependencia, "registro-dependencia"], ["Puesto", r.puesto, "registro-puesto"],
    ["Área", r.area, "registro-area"], ["Entidad", r.entidad, "registro-entidad"],
    ["Fechas del cargo", cargo, "registro-fechas-cargo"], ["Ingresos", r.ingresos, "registro-ingresos"],
  ].filter(([, v]) => v);
  return (
    <section data-testid="ficha-datos-registro" className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">Datos del registro en lista</p>
        {r.simulado && (
          <span data-testid="registro-simulado" className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700">
            <FlaskConical className="h-3 w-3" /> Simulado
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-3" data-testid="registro-lista">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Lista</p>
          <p className="text-sm font-semibold text-slate-900">
            {r.lista_legible}
            {r.lista_codigo && r.lista_codigo !== r.lista_legible && (
              <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">{r.lista_codigo}</span>
            )}
          </p>
        </div>
        <Dato label="Estatus en la lista" value={r.estatus} testid="registro-estatus" />
        <Dato label="Categoría de riesgo" value={r.categoria_riesgo} testid="registro-categoria" />
        <Dato label="ID Q-Detect" value={r.id_qdetect} mono testid="registro-id-qdetect" />
        <Dato label="Fecha de consulta" value={r.fecha_consulta} testid="registro-fecha-consulta" />
        <Dato label="Nombre en el registro" value={r.nombre} testid="registro-nombre" />
        <Dato label="Ámbito" value={r.ambito} testid="registro-ambito" />
        {opcionales.map(([l, v, t]) => <Dato key={t} label={l} value={v} testid={t} />)}
      </div>
      {r.relacionados?.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2" data-testid="registro-relacionados">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Relacionados</p>
          <ul className="mt-1 space-y-0.5 text-sm text-slate-700">
            {r.relacionados.map((x, i) => (
              <li key={`rel-${i}`}>{x.nombre || "—"}{x.parentesco ? <span className="text-slate-500"> · {x.parentesco}</span> : null}</li>
            ))}
          </ul>
        </div>
      )}
      {r.detalle_coincidencia && (
        <p className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500" data-testid="registro-detalle">
          Detalle de la coincidencia: {typeof r.detalle_coincidencia === "string"
            ? r.detalle_coincidencia : JSON.stringify(r.detalle_coincidencia)}
        </p>
      )}
    </section>
  );
}

function Valor({ v }) {
  return <span className="break-all">{v || <span className="text-slate-400">—</span>}</span>;
}

export function ComparativoIdentidad({ filas, tipoPersona, origen }) {
  const quien = tipoPersona || "Cliente / Prospecto";
  const etiqueta = (f) => (f.campo === "nombre" && f.porcentaje != null ? `${f.etiqueta} (${f.porcentaje}%)` : f.etiqueta);
  return (
    <section data-testid="comparativo-tabla" className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">Comparativo de identidad</p>
        {origen === "dictamen" && <p className="text-[11px] text-slate-400">Foto del comparativo al momento del dictamen</p>}
      </div>
      <table className="hidden w-full text-sm sm:table">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-400">
            <th className="py-1.5 pr-2 font-medium">Dato</th>
            <th className="py-1.5 pr-2 font-medium">{quien}</th>
            <th className="py-1.5 pr-2 font-medium">Registro en lista</th>
            <th className="py-1.5 font-medium">Resultado</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.campo} data-testid={`comparativo-fila-${f.campo}`} className="border-b border-slate-100 last:border-0">
              <td className="py-2 pr-2 text-xs font-medium text-slate-600">{etiqueta(f)}</td>
              <td className="py-2 pr-2 text-slate-800"><Valor v={f.persona} /></td>
              <td className="py-2 pr-2 text-slate-800"><Valor v={f.registro} /></td>
              <td className="py-2"><EstadoBadge estado={f.estado} testid={`comparativo-estado-${f.campo}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="space-y-2 sm:hidden">
        {filas.map((f) => (
          <li key={f.campo} data-testid={`comparativo-movil-${f.campo}`} className="rounded-md border border-slate-100 p-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-slate-600">{etiqueta(f)}</span>
              <EstadoBadge estado={f.estado} />
            </div>
            <p className="mt-1 text-xs text-slate-500">{quien}: <span className="text-slate-800"><Valor v={f.persona} /></span></p>
            <p className="text-xs text-slate-500">Registro: <span className="text-slate-800"><Valor v={f.registro} /></span></p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AvisoHomonimo({ texto }) {
  return (
    <div data-testid="aviso-homonimo" className="flex items-start gap-2 rounded-md border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="font-medium">{texto}</span>
    </div>
  );
}

async function verEvidencia(id) {
  try {
    const { data } = await api.post(`/documentos/${id}/signed-url`);
    window.open(`${BACKEND_URL}${data.url}`, "_blank");
  } catch (e) { toast.error(apiError(e)); }
}

export function DictamenRegistrado({ dictamen: d }) {
  return (
    <section data-testid="dictamen-registrado" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">Dictamen registrado</p>
      <p className="mt-1 font-semibold text-slate-900" data-testid="dictamen-decision">{d.decision_texto}</p>
      {d.motivos_texto?.length > 0 && (
        <p className="mt-1 text-xs text-slate-600" data-testid="dictamen-motivos">Motivos: {d.motivos_texto.join(" · ")}</p>
      )}
      {d.justificacion && <p className="mt-1 text-xs text-slate-700" data-testid="dictamen-justificacion-registrada">“{d.justificacion}”</p>}
      <p className="mt-1 text-[11px] text-slate-500">
        {d.usuario || "—"} · {String(d.fecha_hora || "").slice(0, 19).replace("T", " ") || "—"}
      </p>
      {d.evidencia?.id && (
        <button type="button" data-testid="dictamen-evidencia-ver" onClick={() => verEvidencia(d.evidencia.id)}
          className="mt-1 text-xs font-medium text-primary underline-offset-2 hover:underline">
          Evidencia: {d.evidencia.nombre_archivo}
        </button>
      )}
      {d.legado && (
        <p className="mt-2 text-[11px] italic text-slate-500">Dictamen registrado antes de la constancia estructurada.</p>
      )}
    </section>
  );
}

export function ConstanciaButton({ matchId, folio, testid, size = "sm", compact = false }) {
  const [busy, setBusy] = useState(false);
  const descargar = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    try {
      const r = await api.get(`/screening/matches/${matchId}/constancia-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Constancia_${r.headers?.["x-constancia-folio"] || folio || "dictamen"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      let msg = "No se pudo generar la constancia";
      try { msg = JSON.parse(await err?.response?.data?.text())?.detail || msg; } catch { /* blob no JSON */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <Button type="button" variant="outline" size={size} onClick={descargar} disabled={busy} title={folio || undefined}
      data-testid={testid || `constancia-descargar-${matchId}`} className="gap-1.5">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
      {compact ? "Constancia" : folio ? `Constancia ${folio}` : "Constancia PDF"}
    </Button>
  );
}
